import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import Loading from "./Loading";
import { useEffect, useState } from "react";
import { maskMoney, unMaskMoney } from "../helpers/format";
import moment from "moment";
import { couponChangeStatus, couponUpdate } from "../services/coupons";

const CouponsDetails = ({
  open,
  data,
  onClose,
  refetch,
}: {
  open: boolean;
  data: {
    id: string;
    code: string;
    cuponsUser: number;
    limit: number;
    discount: number;
    discountType: string;
    validate: Date;
    type: string;
    firstMonthlyPayment: boolean;
    statusCupom: string;
  };
  onClose: () => void;
  refetch: () => void;
}) => {
  const [value, setValue] = useState("1");
  const [type, setType] = useState("PLAN");
  const [discountType, setDiscountType] = useState("MONEY");
  const [firstMonthlyPayment, setFirstMonthlyPayment] = useState("false");
  const [checkedLimit, setCheckedLimit] = useState(false);
  const [checkedValidate, setCheckedValidate] = useState(false);
  const [statusCupom, setStatusCupom] = useState("");

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const disableCouponStatus = () => {
    setStatusCupom("EXPIRED");
    mutateCouponStatus({ id: values.id, statusCupom: "EXPIRED" });
  };

  const enableCouponStatus = () => {
    setStatusCupom("AVAILABLE");
    mutateCouponStatus({ id: values.id, statusCupom: "AVAILABLE" });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: couponUpdate,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Cupom cadastrado");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const { mutate: mutateCouponStatus, isPending: isPendingCouponStatus } =
    useMutation({
      mutationFn: couponChangeStatus,
      onSuccess: () => {
        onClose();
        refetch();
        toast.success("Status do cupom alterado");
      },

      onError: (e: { response: { data: { message: string } } }) => {
        toast.error(String(e?.response?.data?.message) || "Erro!");
      },
    });

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      id: "",
      code: "",
      limit: "",
      discount: "",
      validate: moment().format("YYYY-MM-DD").toString(),
    },
    onSubmit: (values) => {
      mutate({
        ...values,
        ...(checkedLimit ? { limit: values.limit } : { limit: null }),
        ...(checkedValidate
          ? { validate: moment(values.validate).toDate() }
          : { validate: null }),
        discount: Number(unMaskMoney(values.discount)),
        discountType,
        type,
        firstMonthlyPayment,
      });
    },
  });

  useEffect(() => {
    if (data) {
      setFieldValue("id", data?.id);
      setFieldValue("code", data?.code);
      setFieldValue("limit", data?.limit);
      setFieldValue(
        "discount",
        data?.discount != null ? String(data.discount.toFixed(2)) : ""
      );
      setFieldValue(
        "validate",
        moment(data.validate).format("YYYY-MM-DD").toString()
      );
      setDiscountType(data?.discountType);
      setType(data?.type);
      setFirstMonthlyPayment(String(data?.firstMonthlyPayment));
      setStatusCupom(data?.statusCupom);

      if (data.limit) {
        setCheckedLimit(true);
      }
      if (data.validate) {
        setCheckedValidate(true);
      }
    }
  }, [data]);  

  return (
    <Dialog maxWidth="xs" open={open} onClose={() => onClose()}>
      <Loading loading={isPending || isPendingCouponStatus} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Editar cupom
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Informe os dados do cupom que você quer criar.
            </Typography>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                marginBottom: 2,
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Dados gerais" value="1" />
              <Tab label="Tipo de desconto" value="2" />
            </Tabs>
          </Grid2>
          <Grid2 size={12}>
            <Divider />
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                {statusCupom === "AVAILABLE"
                  ? "Desabilitar Cupom"
                  : "Habilitar Cupom"}
              </Typography>
              <Switch
                checked={statusCupom === "AVAILABLE"}
                onClick={() =>
                  statusCupom === "AVAILABLE"
                    ? disableCouponStatus()
                    : enableCouponStatus()
                }
              />
            </Stack>
            <Divider />
          </Grid2>
          {value === "1" && (
            <Grid2 size={12}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <FormControl>
                    <RadioGroup row value={type}>
                      <FormControlLabel
                        value="PLAN"
                        control={<Radio />}
                        label="Planos"
                        onClick={() => setType("PLAN")}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="code"
                    name="code"
                    label="Código"
                    type="text"
                    fullWidth
                    size="small"
                    value={(values.code || "").toUpperCase()}
                    onChange={handleChange}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Stack>
                    <Typography fontWeight={600} fontSize={12}>
                      Cupom de primeira mensalidade?
                    </Typography>
                    <FormControl>
                      <RadioGroup row value={firstMonthlyPayment}>
                        <FormControlLabel
                          value="true"
                          control={<Radio />}
                          label="Sim"
                          onClick={() => setFirstMonthlyPayment("true")}
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio />}
                          label="Não"
                          onClick={() => setFirstMonthlyPayment("false")}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                </Grid2>
                <Grid2 size={12}>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontSize={14}>
                      Possui limite?
                    </Typography>
                    <Checkbox
                      checked={checkedLimit}
                      onChange={() => setCheckedLimit(!checkedLimit)}
                    />
                  </Stack>
                </Grid2>
                {checkedLimit && (
                  <Grid2 size={12}>
                    <Input
                      id="limit"
                      name="limit"
                      label="Limite"
                      type="text"
                      fullWidth
                      size="small"
                      value={values.limit}
                      onChange={handleChange}
                    />
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body1" fontSize={14}>
                      Possui validade?
                    </Typography>
                    <Checkbox
                      checked={checkedValidate}
                      onChange={() => setCheckedValidate(!checkedValidate)}
                    />
                  </Stack>
                </Grid2>
                {checkedValidate && (
                  <Grid2 size={12}>
                    <Input
                      id="validate"
                      name="validate"
                      label="Validade"
                      type="date"
                      fullWidth
                      size="small"
                      value={values.validate}
                      onChange={handleChange}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.showPicker()
                      }
                    />
                  </Grid2>
                )}
                <Grid2 size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (!values.code) {
                        toast.error("Digite o código do cupom");
                        return;
                      }
                      setValue("2");
                    }}
                  >
                    Próximo
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          )}
          {value === "2" && (
            <Grid2 size={12}>
              <Grid2 container spacing={2}>
                <Grid2 size={12}>
                  <FormControl>
                    <RadioGroup row value={discountType}>
                      <FormControlLabel
                        value="MONEY"
                        control={<Radio />}
                        label="Dinheiro R$"
                        onClick={() => {
                          setFieldValue("discount", "");
                          setDiscountType("MONEY");
                        }}
                      />
                      <FormControlLabel
                        value="PERCENTAGE"
                        control={<Radio />}
                        label="Porcentagem %"
                        onClick={() => {
                          setFieldValue("discount", "");
                          setDiscountType("PERCENTAGE");
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid2>
                <Grid2 size={12}>
                  <Input
                    id="discount"
                    name="discount"
                    label="Desconto"
                    type="text"
                    fullWidth
                    size="small"
                    value={values.discount}
                    onChange={(e) => {
                      setFieldValue("discount", maskMoney(e.target.value));
                    }}
                  />
                </Grid2>
                <Grid2 size={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => {
                      if (!values.discount) {
                        toast.error("Digite o desconto");
                        return;
                      }

                      handleSubmit();
                    }}
                  >
                    Editar cupom
                  </Button>
                </Grid2>
              </Grid2>
            </Grid2>
          )}
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default CouponsDetails;
